import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { collectionI18n } from './i18n';
import { dummyCollectionsData } from './dummyCollectionsData';
import CardStats from '../../components/CardStats/CardStats';

export default function Collections() {
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Dropdown, Table, Badge, IconButton, Modal } =
    DesignSystemComponents ?? {};

  const { t } = useScopedTranslation(collectionI18n);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    setTableLoading(true);
    setRows(dummyCollectionsData);
    setTableLoading(false);
  }, []);

  const serviceOptions = [
    { label: t('col_allServices'), value: 'all' },
    {
      label: 'Asesoría Legal Corporativa',
      value: 'Asesoría Legal Corporativa',
    },
    { label: 'Litigio Civil', value: 'Litigio Civil' },
    { label: 'Defensa Laboral', value: 'Defensa Laboral' },
    { label: 'Asesoría Tributaria', value: 'Asesoría Tributaria' },
    {
      label: 'Regularización Inmobiliaria',
      value: 'Regularización Inmobiliaria',
    },
  ];
  const [selectedService, setSelectedService] = useState(serviceOptions[0]);

  const [showCreate, setShowCreate] = useState(false);

  const currencyCL = (n) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(n);

  const statusColor = (s) =>
    s === 'Vigente' ? 'info' : s === 'Pendiente' ? 'warning' : 'error';

  const kpi = useMemo(() => {
    let current = 0;
    let pending = 0;
    let expired = 0;
    rows.forEach((r) => {
      if (r.estado === 'Vigente') current += 1;
      else if (r.estado === 'Pendiente') pending += 1;
      else expired += 1;
    });
    return { current, pending, expired };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesText =
        !q ||
        r.cliente.toLowerCase().includes(q) ||
        r.servicio.toLowerCase().includes(q);
      const matchesService =
        selectedService.value === 'all' || r.servicio === selectedService.value;
      return matchesText && matchesService;
    });
  }, [rows, search, selectedService]);

  if (!Card || !Button || !Input || !Dropdown || !Table || !Badge) return null;

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start mb-4'>
        <div>
          <h2 className=' fw-semibold'>{t('col_title')}</h2>
          <h4 className=' fw-normal'>{t('col_subtitle')}</h4>
        </div>
        <Button
          label={t('col_newCollection')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => setShowCreate(true)}
        />
      </div>

      <div className='row g-3 mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('col_statCurrent')}
            count={kpi.current}
            subtitle={t('col_statCurrentSub')}
            icon='creditCard'
            stroke
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('col_statPending')}
            count={kpi.pending}
            subtitle={t('col_statPendingSub')}
            icon='clock'
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('col_statExpired')}
            count={kpi.expired}
            subtitle={t('col_statExpiredSub')}
            icon='infoCircle'
            fill
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        <div className='row g-3 mb-3'>
          <div className='col-12 col-md-6'>
            <Input
              icon='search'
              placeholder={t('col_searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className='col-12 col-md-3'>
            <Dropdown
              options={serviceOptions}
              value={selectedService}
              onChange={setSelectedService}
              placeholder={t('col_allServices')}
              width='100%'
            />
          </div>
        </div>

        <div className='table-container'>
          <Table
            columns={[
              { accessor: 'cliente', label: t('col_col_client'), width: '24%' },
              { accessor: 'servicio', label: t('col_col_service'), width: '22%' },
              {
                accessor: 'montoPendiente',
                label: t('col_col_pendingAmount'),
                width: '14%',
                align: 'right',
                render: (row) => currencyCL(row.montoPendiente),
              },
              {
                accessor: 'cuotas',
                label: t('col_col_installment'),
                width: '10%',
                align: 'center',
              },
              {
                accessor: 'vencimiento',
                label: t('col_col_dueDate'),
                width: '14%',
                render: (row) =>
                  new Date(row.vencimiento).toLocaleDateString(),
              },
              {
                accessor: 'estado',
                label: t('col_col_status'),
                width: '10%',
                render: (row) => (
                  <Badge
                    text={row.estado}
                    color={statusColor(row.estado)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('col_col_actions'),
                width: '10%',
                align: 'center',
                render: (row) => (
                  <div className='d-flex justify-content-center gap-2'>
                    <IconButton
                      ariaLabel={t('col_aria_edit')}
                      icon='pencil'
                      onClick={() => {
                        setShowCreate(true);
                      }}
                      iconStroke='var(--primary-500)'
                    />
                    <IconButton
                      ariaLabel={t('col_aria_view')}
                      icon='eye'
                      onClick={() => {}}
                    />
                  </div>
                ),
              },
            ]}
            data={filtered}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            loading={tableLoading}
          />
          {!tableLoading && filtered.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('col_empty')}</p>
            </div>
          )}
        </div>
      </Card>

      {Modal ? (
        <Modal
          title={t('col_modal_title')}
          show={showCreate}
          onClose={() => setShowCreate(false)}
          secondaryBtnTxt={t('col_modal_cancel')}
          secondaryBtnAction={() => setShowCreate(false)}
          primaryBtnTxt={t('col_modal_save')}
          primaryBtnAction={() => setShowCreate(false)}
          width='640px'
        >
          <p className='mb-0 caption'>
            {t('col_modal_placeholder')}
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
